import React, { useState, useEffect, useCallback, useRef } from 'react';
import { psrReportAPI } from '../../services/api';

const MONTHS = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_BADGE = {
  draft:             'secondary',
  submitted:         'warning',
  supervisor_review: 'warning',
  under_review:      'warning',
  approved:          'success',
  rejected:          'danger',
};

const STATUS_LABEL = {
  draft:             'Draft',
  submitted:         'Submitted',
  supervisor_review: 'Under Review',
  under_review:      'Under Review',
  approved:          'Approved',
  rejected:          'Rejected',
};

const PAGE_SIZE = 10;

export default function PSRReview() {
  const [reports, setReports]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage]               = useState(1);

  // Review modal state
  const [selected, setSelected]       = useState(null);  // report being reviewed
  const [action, setAction]           = useState('');     // 'approve' | 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [comments, setComments]       = useState('');
  const [supCallDone, setSupCallDone] = useState(false);
  const [videoLink, setVideoLink]     = useState('');
  const [screenshot, setScreenshot]   = useState(null);  // File object
  const [reviewing, setReviewing]     = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState('');
  const screenshotRef = useRef();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await psrReportAPI.getPending();
      setReports(res.data?.data?.reports || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load reports.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [filterStatus]);

  // Derived
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear  = now.getFullYear();

  const pendingCount  = reports.filter(r => r.status === 'submitted' || r.status === 'supervisor_review' || r.status === 'under_review').length;
  const approvedThisMonth = reports.filter(r =>
    r.status === 'approved' &&
    parseInt(r.report_month) === currentMonth &&
    parseInt(r.report_year) === currentYear
  ).length;

  const filtered = filterStatus === 'all'
    ? reports
    : reports.filter(r => {
        if (filterStatus === 'submitted') return r.status === 'submitted' || r.status === 'supervisor_review' || r.status === 'under_review';
        return r.status === filterStatus;
      });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Open review panel
  const openReview = (report) => {
    setSelected(report);
    setAction('');
    setRejectionReason('');
    setComments('');
    setSupCallDone(false);
    setVideoLink('');
    setScreenshot(null);
    setReviewError('');
    setReviewSuccess('');
  };

  const closeModal = () => {
    setSelected(null);
    setAction('');
    setRejectionReason('');
    setComments('');
    setSupCallDone(false);
    setVideoLink('');
    setScreenshot(null);
    setReviewError('');
    setReviewSuccess('');
  };

  const handleReviewSubmit = async () => {
    if (!action) { setReviewError('Please choose Approve or Reject.'); return; }
    if (action === 'reject' && !rejectionReason.trim()) {
      setReviewError('Rejection reason is required.');
      return;
    }
    setReviewing(true);
    setReviewError('');
    setReviewSuccess('');
    try {
      const fd = new FormData();
      fd.append('psr_id',   String(selected.id));
      fd.append('decision', action);
      if (action === 'reject') fd.append('rejection_reason', rejectionReason);
      if (comments)            fd.append('comments', comments);
      fd.append('supervisor_call_completed', supCallDone ? '1' : '0');
      fd.append('supervisor_video_link',     videoLink || '');
      if (screenshot) fd.append('supervisor_screenshot', screenshot);
      await psrReportAPI.review(fd);
      setReviewSuccess(action === 'approve' ? 'Report approved successfully.' : 'Report rejected.');
      await fetchReports();
      setTimeout(() => closeModal(), 1200);
    } catch (err) {
      setReviewError(err.response?.data?.message || 'Failed to submit review.');
    } finally {
      setReviewing(false);
    }
  };

  const isReviewable = (r) => r.status === 'submitted' || r.status === 'supervisor_review' || r.status === 'under_review';

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-0">Review PSRs</h2>
        <small className="text-muted">Review and approve monthly reports from your employees</small>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-lg-3">
          <div className="card border-0 bg-warning bg-opacity-10 h-100">
            <div className="card-body d-flex align-items-center gap-3">
              <div className="bg-warning rounded-3 d-flex align-items-center justify-content-center text-white"
                style={{ width: 48, height: 48, flexShrink: 0 }}>
                <i className="bi bi-hourglass-split fs-5" />
              </div>
              <div>
                <div className="fs-3 fw-bold lh-1">{pendingCount}</div>
                <div className="text-muted small">Pending Review</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="card border-0 bg-success bg-opacity-10 h-100">
            <div className="card-body d-flex align-items-center gap-3">
              <div className="bg-success rounded-3 d-flex align-items-center justify-content-center text-white"
                style={{ width: 48, height: 48, flexShrink: 0 }}>
                <i className="bi bi-check-circle fs-5" />
              </div>
              <div>
                <div className="fs-3 fw-bold lh-1">{approvedThisMonth}</div>
                <div className="text-muted small">Approved This Month</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="card mb-3">
        <div className="card-body py-2 d-flex align-items-center gap-3 flex-wrap">
          <span className="fw-semibold text-muted small me-1">Filter:</span>
          {[
            { value: 'all',       label: 'All' },
            { value: 'submitted', label: 'Pending Review' },
            { value: 'approved',  label: 'Approved' },
            { value: 'rejected',  label: 'Rejected' },
          ].map(opt => (
            <button
              key={opt.value}
              className={`btn btn-sm ${filterStatus === opt.value ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFilterStatus(opt.value)}
            >
              {opt.label}
              {opt.value === 'all' && <span className="badge bg-secondary ms-1">{reports.length}</span>}
              {opt.value === 'submitted' && <span className="badge bg-warning text-dark ms-1">{pendingCount}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" />
              <div className="mt-2 text-muted">Loading reports…</div>
            </div>
          ) : error ? (
            <div className="alert alert-danger m-3">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-clipboard-x fs-1 d-block mb-2" />
              No reports found for the selected filter.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Employee</th>
                      <th>Month / Year</th>
                      <th>Client</th>
                      <th>Role</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(r => (
                      <tr key={r.id}>
                        <td>
                          <div className="fw-semibold">{r.employee_name || r.user_name || '—'}</div>
                          <div className="text-muted small">{r.employee_id || r.email || ''}</div>
                        </td>
                        <td className="fw-semibold">
                          {MONTHS[parseInt(r.report_month)] || r.report_month} {r.report_year}
                        </td>
                        <td>{r.client || <span className="text-muted">—</span>}</td>
                        <td>{r.role_title || r.job_title || <span className="text-muted">—</span>}</td>
                        <td className="text-muted small">
                          {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          <span className={`badge bg-${STATUS_BADGE[r.status] || 'secondary'}`}>
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                        </td>
                        <td>
                          {isReviewable(r) ? (
                            <button className="btn btn-sm btn-primary" onClick={() => openReview(r)}>
                              <i className="bi bi-clipboard-check me-1" />Review
                            </button>
                          ) : (
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => openReview(r)}>
                              <i className="bi bi-eye me-1" />View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
                  <small className="text-muted">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                  </small>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p - 1)}>
                          <i className="bi bi-chevron-left" />
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p + 1)}>
                          <i className="bi bi-chevron-right" />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {selected && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={closeModal} />
          <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-clipboard-check me-2" />
                    PSR Report — {MONTHS[parseInt(selected.report_month)]} {selected.report_year}
                  </h5>
                  <button className="btn-close" onClick={closeModal} disabled={reviewing} />
                </div>
                <div className="modal-body">
                  {reviewError   && <div className="alert alert-danger py-2">{reviewError}</div>}
                  {reviewSuccess && <div className="alert alert-success py-2">{reviewSuccess}</div>}

                  {/* PSR Details */}
                  <div className="row g-3 mb-4">
                    <div className="col-12">
                      <h6 className="text-muted text-uppercase small fw-semibold border-bottom pb-1 mb-3">Report Details</h6>
                    </div>
                    <DetailField label="Employee"    value={selected.employee_name || selected.user_name} />
                    <DetailField label="Month / Year" value={`${MONTHS[parseInt(selected.report_month)]} ${selected.report_year}`} />
                    <DetailField label="Role / Title" value={selected.role_title || selected.job_title} />
                    <DetailField label="Client"      value={selected.client} />
                    <DetailField label="Vendor"      value={selected.vendor} />
                    <DetailField label="Technology"  value={selected.technology} wide />
                    <DetailField label="Project Description" value={selected.project_description} wide />
                    <DetailField label="Tools Used"  value={selected.tools_used} wide />
                    <DetailField label="Tasks Completed This Month" value={selected.tasks_this_month} wide />
                    <DetailField label="Tasks for Coming Month"     value={selected.tasks_next_month} wide />
                    <DetailField label="Issues"      value={selected.issues} wide />
                    {(selected.i9_changes_required === 1 || selected.i9_changes_required === '1') && (
                      <DetailField label="I-9 Change Reason" value={selected.i9_change_reason} wide />
                    )}
                    {selected.github_link && (
                      <div className="col-md-6">
                        <span className="fw-semibold small text-muted d-block">GitHub Link</span>
                        <a href={selected.github_link} target="_blank" rel="noreferrer" className="small">{selected.github_link}</a>
                      </div>
                    )}
                    <div className="col-md-6">
                      <span className="fw-semibold small text-muted d-block">I-9 Changes Required</span>
                      <span className={`badge ${(selected.i9_changes_required === 1 || selected.i9_changes_required === '1') ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                        {(selected.i9_changes_required === 1 || selected.i9_changes_required === '1') ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>

                  {/* Review Actions (only for reviewable reports) */}
                  {isReviewable(selected) && (
                    <>
                      <h6 className="text-muted text-uppercase small fw-semibold border-bottom pb-1 mb-3">Supervisor Notes</h6>

                      <div className="mb-3 form-check">
                        <input type="checkbox" className="form-check-input" id="supCallDone"
                          checked={supCallDone} onChange={e => setSupCallDone(e.target.checked)}
                          disabled={reviewing} />
                        <label className="form-check-label fw-semibold" htmlFor="supCallDone">
                          Supervisor Call Completed
                        </label>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Video Recording Link <span className="text-muted fw-normal">(optional)</span>
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          value={videoLink}
                          onChange={e => setVideoLink(e.target.value)}
                          placeholder="https://drive.google.com/… or meeting recording link"
                          disabled={reviewing}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Attach Screenshot of Meeting <span className="text-muted fw-normal">(optional)</span>
                        </label>
                        <div
                          className="border rounded p-3 text-center"
                          style={{ cursor: 'pointer', borderStyle: 'dashed', background: '#fafafa' }}
                          onClick={() => screenshotRef.current?.click()}
                        >
                          {screenshot
                            ? <span className="text-primary small fw-semibold">{screenshot.name}</span>
                            : <span className="text-muted small">Click to attach screenshot (JPG, PNG)</span>}
                        </div>
                        <input ref={screenshotRef} type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => setScreenshot(e.target.files[0] || null)} disabled={reviewing} />
                      </div>

                      <h6 className="text-muted text-uppercase small fw-semibold border-bottom pb-1 mb-3">Your Decision</h6>
                      <div className="d-flex gap-3 mb-3">
                        <button
                          className={`btn flex-fill ${action === 'approve' ? 'btn-success' : 'btn-outline-success'}`}
                          onClick={() => setAction('approve')}
                          disabled={reviewing}
                        >
                          <i className="bi bi-check-circle me-2" />Approve
                        </button>
                        <button
                          className={`btn flex-fill ${action === 'reject' ? 'btn-danger' : 'btn-outline-danger'}`}
                          onClick={() => setAction('reject')}
                          disabled={reviewing}
                        >
                          <i className="bi bi-x-circle me-2" />Reject
                        </button>
                      </div>

                      {action === 'reject' && (
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Rejection Reason <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={rejectionReason}
                            onChange={e => setRejectionReason(e.target.value)}
                            placeholder="Explain why this report is being rejected…"
                            disabled={reviewing}
                          />
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Comments <span className="text-muted fw-normal">(optional)</span>
                        </label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={comments}
                          onChange={e => setComments(e.target.value)}
                          placeholder="Additional comments for the employee…"
                          disabled={reviewing}
                        />
                      </div>
                    </>
                  )}

                  {/* Show existing review info for non-pending */}
                  {!isReviewable(selected) && (
                    <div className="mt-3">
                      <h6 className="text-muted text-uppercase small fw-semibold border-bottom pb-1 mb-3">Review Notes</h6>
                      <div className="row g-2 mb-3">
                        <div className="col-md-6">
                          <span className="fw-semibold small text-muted d-block">Supervisor Call Completed</span>
                          <span className={`badge ${(selected.supervisor_call_completed === 1 || selected.supervisor_call_completed === '1') ? 'bg-success' : 'bg-secondary'}`}>
                            {(selected.supervisor_call_completed === 1 || selected.supervisor_call_completed === '1') ? 'Yes' : 'No'}
                          </span>
                        </div>
                        {selected.supervisor_video_link && (
                          <div className="col-md-6">
                            <span className="fw-semibold small text-muted d-block">Video Recording</span>
                            <a href={selected.supervisor_video_link} target="_blank" rel="noreferrer" className="small">{selected.supervisor_video_link}</a>
                          </div>
                        )}
                        {selected.supervisor_screenshot && (
                          <div className="col-12">
                            <span className="fw-semibold small text-muted d-block mb-1">Meeting Screenshot</span>
                            <img
                              src={`${process.env.REACT_APP_API_URL || ''}/${selected.supervisor_screenshot}`}
                              alt="Meeting screenshot"
                              style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, border: '1px solid #e2e8f0' }}
                            />
                          </div>
                        )}
                      </div>
                      {selected.rejection_reason && (
                        <div className="alert alert-danger py-2">
                          <strong>Rejection Reason:</strong> {selected.rejection_reason}
                        </div>
                      )}
                      {selected.reviewer_comments && (
                        <div className="alert alert-info py-2">
                          <strong>Comments:</strong> {selected.reviewer_comments}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={closeModal} disabled={reviewing}>
                    Close
                  </button>
                  {isReviewable(selected) && (
                    <button
                      className={`btn ${action === 'approve' ? 'btn-success' : action === 'reject' ? 'btn-danger' : 'btn-primary'}`}
                      onClick={handleReviewSubmit}
                      disabled={reviewing || !action}
                    >
                      {reviewing
                        ? <><span className="spinner-border spinner-border-sm me-2" />Submitting…</>
                        : <><i className="bi bi-send me-2" />Submit Review</>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DetailField({ label, value, wide }) {
  return (
    <div className={wide ? 'col-12' : 'col-md-6'}>
      <span className="fw-semibold small text-muted d-block">{label}</span>
      <span>{value || <span className="text-muted">—</span>}</span>
    </div>
  );
}
